import type { Todo } from "../../domain/entities.ts";
import { toggleSelection } from "./selection.ts";
import { selectedTaskFamilies } from "./task-hierarchy.ts";

export type BulkUndoField = "completed" | "completedAt" | "pinned" | "archivedAt" | "deleted" | "categoryId" | "position" | "dueDate" | "startDate" | "startAt" | "endAt" | "estimatedMinutes";

export interface BulkActionCapabilities<Snapshot> {
  tasks(): Todo[];
  selectedIds(): Set<string>;
  setSelectedIds(ids: Set<string>): void;
  setSelectionMode(active: boolean): void;
  activeRoot(): string | null;
  canEdit(id: string): boolean;
  blockers(todo: Todo): Todo[];
  approvalRequired?(todo: Todo): boolean;
  complete(todo: Todo): Promise<string | null>;
  mutate(id: string, update: (todo: Todo) => void): Promise<unknown>;
  mutateBatch?(items: Array<{ id: string; rootId: string; mutate(todo: Todo): void }>): Promise<unknown>;
  snapshot(todo: Todo): Snapshot;
  confirmDelete(count: number): Promise<boolean>;
  render(): Promise<void>;
  focusSelection(id: string): void;
  undo(message: string, rootId: string, before: Snapshot[], fields: BulkUndoField[], generatedIds: string[]): void;
  toast(message: string): void;
  sync(): void;
  now(): number;
}

export interface BulkActionController<Snapshot> {
  setSelected(id: string, checked: boolean): Promise<void>;
  finish(message: string, rootId?: string, before?: Snapshot[], fields?: BulkUndoField[], generatedIds?: string[]): Promise<void>;
  apply(actionName: string): Promise<void>;
}

export function createBulkActionController<Snapshot>(capabilities: BulkActionCapabilities<Snapshot>): BulkActionController<Snapshot> {
  const finish = async (message: string, rootId?: string, before: Snapshot[] = [], fields: BulkUndoField[] = [], generatedIds: string[] = []) => {
    capabilities.setSelectionMode(false); capabilities.setSelectedIds(new Set()); await capabilities.render();
    if (rootId && before.length && fields.length) capabilities.undo(message, rootId, before, fields, generatedIds); else capabilities.toast(message);
    capabilities.sync();
  };
  return {
    setSelected: async (id, checked) => { capabilities.setSelectedIds(toggleSelection(capabilities.selectedIds(), id, checked)); await capabilities.render(); capabilities.focusSelection(id); },
    finish,
    apply: async actionName => {
      const tasks = capabilities.tasks();
      const chosen = tasks.filter(todo => capabilities.selectedIds().has(todo.id) && capabilities.canEdit(todo.id));
      const selected = actionName === "bulk-archive" || actionName === "bulk-delete" ? selectedTaskFamilies(tasks, chosen) : chosen;
      const rootId = capabilities.activeRoot(); if (!selected.length || !rootId) return;
      if (selected.some(todo => !capabilities.canEdit(todo.id))) { capabilities.toast("You need edit access to every child task"); return; }
      let before = selected.map(capabilities.snapshot); const generatedIds: string[] = [];
      if (actionName === "bulk-delete" && !await capabilities.confirmDelete(selected.length)) return;
      if (actionName === "bulk-complete") {
        const eligible = selected.filter(item => !item.completed && !capabilities.blockers(item).length && !capabilities.approvalRequired?.(item));
        if (!eligible.length) { capabilities.toast("No selected tasks can be completed yet; check blockers and approvals"); return; }
        before = eligible.map(capabilities.snapshot);
        for (const todo of eligible) { const generatedId = await capabilities.complete(todo); if (generatedId) generatedIds.push(generatedId); }
      } else {
        const update = (draft: Todo) => {
          if (actionName === "bulk-reopen") { draft.completed = false; draft.completedAt = null; }
          if (actionName === "bulk-pin") draft.pinned = true;
          if (actionName === "bulk-unpin") draft.pinned = false;
          if (actionName === "bulk-archive") draft.archivedAt = capabilities.now();
          if (actionName === "bulk-delete") draft.deleted = true;
        };
        if ((actionName === "bulk-archive" || actionName === "bulk-delete") && capabilities.mutateBatch) await capabilities.mutateBatch(selected.map(todo => ({ id: todo.id, rootId: todo.rootId, mutate: update })));
        else await Promise.all(selected.map(todo => capabilities.mutate(todo.id, update)));
      }
      const labels: Record<string, string> = { "bulk-complete": "Tasks completed", "bulk-reopen": "Tasks reopened", "bulk-pin": "Tasks pinned", "bulk-unpin": "Tasks unpinned", "bulk-archive": "Tasks archived", "bulk-delete": "Tasks moved to trash" };
      const fields: Record<string, BulkUndoField[]> = { "bulk-complete": ["completed", "completedAt"], "bulk-reopen": ["completed", "completedAt"], "bulk-pin": ["pinned"], "bulk-unpin": ["pinned"], "bulk-archive": ["archivedAt"], "bulk-delete": ["deleted"] };
      if (labels[actionName] && fields[actionName]) await finish(actionName === "bulk-complete" && before.length < selected.length ? `${before.length} ${before.length === 1 ? "task" : "tasks"} completed · ${selected.length - before.length} skipped (blockers or approval)` : labels[actionName], rootId, before, fields[actionName], generatedIds);
    },
  };
}
